import { NavLink } from 'react-router-dom'
import { MessageSquare, Users, Clock, Target, Calendar, FileText, Settings, LogOut, PanelLeft, ChevronLeft, ChevronRight, UserCog } from 'lucide-react'
import { Button } from './ui/button'
import { useAuthStore, useUIStore } from '@/stores'
import { cn } from '@/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'

const navItems = [
  { title: 'AI 助手', href: '/', icon: MessageSquare, color: 'text-blue-500' },
  { title: '员工管理', href: '/employees', icon: UserCog, color: 'text-teal-500' },
  { title: '考勤管理', href: '/attendance', icon: Clock, color: 'text-orange-500' },
  { title: '绩效管理', href: '/performance', icon: Target, color: 'text-purple-500' },
  { title: '请假管理', href: '/leave', icon: Calendar, color: 'text-pink-500' },
  { title: '文档管理', href: '/documents', icon: FileText, color: 'text-cyan-500' },
]

export function Sidebar() {
  const logout = useAuthStore((state) => state.logout)
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed)
  const sidebarMini = useUIStore((state) => state.sidebarMini)
  const toggleSidebarMini = useUIStore((state) => state.toggleSidebarMini)

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen transition-all duration-300 ease-in-out lg:translate-x-0',
          'bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-r border-slate-200 dark:border-slate-800',
          sidebarCollapsed ? '-translate-x-full' : 'translate-x-0',
          sidebarMini ? 'w-20' : 'w-64'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={cn(
            "flex items-center gap-3 px-6 py-5 border-b border-slate-200 dark:border-slate-800",
            sidebarMini && "px-4 justify-center"
          )}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 shrink-0">
              <span className="text-white font-bold text-lg">E</span>
            </div>
            {!sidebarMini && (
              <div className="overflow-hidden">
                <h1 className="font-semibold text-slate-900 dark:text-white text-sm whitespace-nowrap">Enterprise</h1>
                <p className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">管理系统</p>
              </div>
            )}
          </div>


          {/* Navigation */}
          <nav className={cn(
            "flex flex-col flex-1 overflow-y-auto",
            sidebarMini ? "px-0" : "px-4"
          )}>
            <ul className={cn("flex flex-col gap-6 pt-10", sidebarMini ? "items-center" : "")}>
              {navItems.map((item) => (
                <li key={item.href}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={item.href}
                        className={({ isActive }: { isActive: boolean }) =>
                          cn(
                            'flex items-center rounded-lg text-sm font-medium transition-all duration-200',
                            'hover:bg-slate-100 dark:hover:bg-slate-800',
                            isActive
                              ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                              : 'text-slate-600 dark:text-slate-400',
                            sidebarMini
                              ? 'w-12 h-12 justify-center'
                              : 'px-3 py-3 w-full'
                          )
                        }
                      >
                        <item.icon className={cn('w-5 h-5 shrink-0', item.color)} />
                        {!sidebarMini && <span className="ml-3 whitespace-nowrap">{item.title}</span>}
                      </NavLink>
                    </TooltipTrigger>
                    {sidebarMini && (
                      <TooltipContent side="right" className="ml-2">
                        {item.title}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </li>
              ))}
            </ul>
          </nav>

          {/* Bottom Actions */}
          <div className={cn(
            "border-t border-slate-200 dark:border-slate-800",
            sidebarMini ? "px-0 py-4" : "px-4 py-4"
          )}>
            <ul className={cn("flex flex-col gap-6", sidebarMini ? "items-center" : "")}>
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800",
                        sidebarMini ? "w-12 h-12 justify-center" : "justify-start px-3 py-3"
                      )}
                    >
                      <Settings className="w-5 h-5 shrink-0" />
                      {!sidebarMini && <span className="ml-3">设置</span>}
                    </Button>
                  </TooltipTrigger>
                  {sidebarMini && (
                    <TooltipContent side="right" className="ml-2">设置</TooltipContent>
                  )}
                </Tooltip>
              </li>
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full rounded-lg text-rose-600 dark:text-rose-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-rose-700 dark:hover:text-rose-300",
                        sidebarMini ? "w-12 h-12 justify-center" : "justify-start px-3 py-3"
                      )}
                      onClick={logout}
                    >
                      <LogOut className="w-5 h-5 shrink-0" />
                      {!sidebarMini && <span className="ml-3">退出登录</span>}
                    </Button>
                  </TooltipTrigger>
                  {sidebarMini && (
                    <TooltipContent side="right" className="ml-2">退出登录</TooltipContent>
                  )}
                </Tooltip>
              </li>
            </ul>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  )
}
